'use client';

import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react';

interface ScrapeResults {
  // Profile Identity (8 fields)
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  title?: string | null;
  jobTitle?: string | null;
  company?: string | null;
  nmlsNumber?: string | null;
  yearsLicensed?: string | null;

  // Location (4 fields)
  city?: string | null;
  primaryState?: string | null;
  address?: string | null;
  numStateLicenses?: number | null;

  // Production Stats (11 fields)
  totalVolume?: string | null;
  totalLoans?: string | null;
  avgLoanAmount?: string | null;
  purchase?: string | null;
  purchaseLoans?: string | null;
  refinance?: string | null;
  refinanceLoans?: string | null;
  topProduct?: string | null;
  buyerAgents?: string | null;
  sellerAgents?: string | null;
  timePeriod?: string | null;

  // Contact Info - Internal (2 fields)
  email_internal?: string | null;
  phones_internal?: Array<{label: string; number: string}> | string[] | null;

  // Social Links (2 fields)
  website?: string | null;
  linkedin?: string | null;

  // Debug/Internal URLs
  modelMatchUrl?: string | null;
  profileUrl?: string | null;
  agentsUrl?: string | null;
}

type Status = 'idle' | 'scraping' | 'polling' | 'complete' | 'timeout' | 'error';

interface TimelineEvent {
  label: string;
  timestamp: Date;
  type: 'info' | 'progress' | 'success' | 'error';
}

export default function ScrapeDebugPanel() {
  const [status, setStatus] = useState<Status>('idle');
  const [nmls, setNmls] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [pollCount, setPollCount] = useState(0);
  const [maxPolls, setMaxPolls] = useState(30);
  const [results, setResults] = useState<ScrapeResults | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);

  // Timer for elapsed time
  useEffect(() => {
    if (status === 'scraping' || status === 'polling') {
      const interval = setInterval(() => {
        if (startTime) {
          setElapsedTime((Date.now() - startTime) / 1000);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [status, startTime]);

  // Listen for form events
  useEffect(() => {
    const handleScraperUpdate = (event: CustomEvent) => {
      const { type, nmls: eventNmls, draftId: eventDraftId, pollCount: eventPollCount, maxPolls: eventMaxPolls, data, timestamp } = event.detail;

      switch (type) {
        case 'scraper_started':
          setStatus('scraping');
          setNmls(eventNmls);
          setDraftId(eventDraftId);
          setStartTime(timestamp);
          setResults(null);
          setPollCount(0);
          setTimeline([
            { label: `🚀 Scraper started for NMLS: ${eventNmls}`, timestamp: new Date(timestamp), type: 'info' },
            { label: `📋 Draft ID: ${eventDraftId}`, timestamp: new Date(timestamp), type: 'info' }
          ]);
          break;

        case 'polling':
          setStatus('polling');
          setPollCount(eventPollCount);
          setMaxPolls(eventMaxPolls);
          setTimeline(prev => [
            ...prev.filter(e => !e.label.includes('Checking database')), // Remove previous poll messages
            { label: `🔄 Checking database... (${eventPollCount}/${eventMaxPolls})`, timestamp: new Date(timestamp), type: 'progress' }
          ]);
          break;

        case 'data_complete':
          setStatus('complete');
          setResults(data);
          setTimeline(prev => [
            ...prev,
            { label: `✅ Data extraction complete!`, timestamp: new Date(timestamp), type: 'success' },
            { label: `📊 ${countTotalFields(data)} fields extracted successfully`, timestamp: new Date(timestamp), type: 'success' }
          ]);
          break;

        case 'polling_timeout':
          setStatus('timeout');
          setTimeline(prev => [
            ...prev,
            { label: `⚠️ Polling timeout after 60 seconds`, timestamp: new Date(timestamp), type: 'error' }
          ]);
          break;
      }
    };

    window.addEventListener('scraperUpdate' as any, handleScraperUpdate);
    return () => window.removeEventListener('scraperUpdate' as any, handleScraperUpdate);
  }, []);

  const copyToClipboard = async () => {
    if (!results) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(results, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Field categorization
  const profileIdentityFields = [
    'fullName', 'firstName', 'lastName', 'title', 'company',
    'nmlsNumber', 'yearsLicensed', 'numStateLicenses'
  ];

  const locationFields = [
    'city', 'primaryState', 'address'
  ];

  const productionStatsFields = [
    'totalVolume', 'totalLoans', 'avgLoanAmount', 'purchase',
    'purchaseLoans', 'refinance', 'refinanceLoans', 'topProduct',
    'buyerAgents', 'sellerAgents', 'timePeriod'
  ];

  const contactInfoFields = ['email_internal', 'phones_internal'];
  const socialLinksFields = ['website', 'linkedin'];
  const debugInfoFields = ['modelMatchUrl', 'profileUrl', 'agentsUrl'];

  const countWorkingFields = (fields: string[], data: any) => {
    if (!data) return { working: 0, total: fields.length };
    const working = fields.filter(field => {
      const value = data[field as keyof ScrapeResults];
      return value !== null && value !== undefined;
    }).length;
    return { working, total: fields.length };
  };

  const countTotalFields = (data: any) => {
    if (!data) return 0;
    const allFields = [
      ...profileIdentityFields,
      ...locationFields,
      ...productionStatsFields,
      ...contactInfoFields,
      ...socialLinksFields
    ];
    return allFields.filter(field => {
      const value = data[field as keyof ScrapeResults];
      return value !== null && value !== undefined;
    }).length;
  };

  const fieldLabels: Record<string, string> = {
    fullName: 'Full Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    title: 'Job Title',
    company: 'Company',
    nmlsNumber: 'NMLS Number',
    yearsLicensed: 'Years Licensed',
    numStateLicenses: 'Number of State Licenses',
    city: 'City',
    primaryState: 'Primary State',
    address: 'Address',
    totalVolume: 'Total Volume',
    totalLoans: 'Total Loans',
    avgLoanAmount: 'Average Loan Amount',
    purchase: 'Purchase %',
    purchaseLoans: 'Purchase Loans',
    refinance: 'Refinance %',
    refinanceLoans: 'Refinance Loans',
    topProduct: 'Top Product',
    buyerAgents: 'Buyer Agents',
    sellerAgents: 'Seller Agents',
    timePeriod: 'Time Period',
    email_internal: 'Email (Internal)',
    phones_internal: 'Phone Numbers (Internal)',
    website: 'Website',
    linkedin: 'LinkedIn Profile',
    profileUrl: 'Profile URL',
    agentsUrl: 'Agents URL',
    modelMatchUrl: 'Model-Match Profile URL'
  };

  const renderField = (fieldName: string, value: any) => {
    const isNull = value === null || value === undefined;
    const icon = isNull ? '❌' : '✅';
    const displayLabel = fieldLabels[fieldName] || fieldName;

    // Special handling for phones_internal array
    if (fieldName === 'phones_internal' && Array.isArray(value) && value.length > 0) {
      return (
        <div key={fieldName} className="py-1">
          <div className="flex items-start gap-2 mb-1">
            <span className="text-sm">{icon}</span>
            <span className="font-mono text-sm text-gray-700 flex-shrink-0">
              {displayLabel}:
            </span>
          </div>
          <div className="ml-7 space-y-0.5">
            {value.map((phone, idx) => {
              if (typeof phone === 'object' && phone !== null) {
                return (
                  <div key={idx} className="flex items-center gap-1.5">
                    <span className="text-gray-600 text-sm">•</span>
                    <span className="text-sm font-medium text-gray-700 mr-1">{phone.label}:</span>
                    <span className="text-sm text-gray-900">{phone.number}</span>
                  </div>
                );
              } else {
                return (
                  <div key={idx} className="flex items-center gap-1.5">
                    <span className="text-gray-600 text-sm">•</span>
                    <span className="text-sm text-gray-900">{phone}</span>
                  </div>
                );
              }
            })}
          </div>
        </div>
      );
    }

    // Special handling for website and linkedin (clickable links)
    if ((fieldName === 'website' || fieldName === 'linkedin' || fieldName === 'profileUrl' || fieldName === 'agentsUrl' || fieldName === 'modelMatchUrl') && !isNull) {
      const url = String(value);
      return (
        <div key={fieldName} className="flex items-start gap-2 py-1">
          <span className="text-sm">{icon}</span>
          <span className="font-mono text-sm text-gray-700 flex-shrink-0">
            {displayLabel}:
          </span>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 underline break-all flex-1"
          >
            {url}
          </a>
        </div>
      );
    }

    // Default handling
    let displayValue = 'null';
    if (!isNull) {
      if (Array.isArray(value)) {
        displayValue = JSON.stringify(value);
      } else if (typeof value === 'object') {
        displayValue = JSON.stringify(value);
      } else {
        displayValue = String(value);
      }
    }

    return (
      <div key={fieldName} className="flex items-start gap-2 py-1">
        <span className="text-sm">{icon}</span>
        <span className="font-mono text-sm text-gray-700 flex-shrink-0">
          {displayLabel}:
        </span>
        <span className={`text-sm flex-1 ${isNull ? 'text-gray-400 italic' : 'text-gray-900'}`}>
          {isNull ? 'null' : `"${displayValue}"`}
        </span>
      </div>
    );
  };

  const renderCategory = (title: string, emoji: string, fields: string[]) => {
    if (!results) return null;
    const { working, total } = countWorkingFields(fields, results);

    return (
      <div className="mb-6">
        <h3 className="font-bold text-base mb-3 text-gray-900">
          {emoji} {title} ({working}/{total} fields working)
        </h3>
        <div className="space-y-1">
          {fields.map(field => renderField(field, results[field as keyof ScrapeResults]))}
        </div>
      </div>
    );
  };

  const getStatusColor = () => {
    switch (status) {
      case 'scraping': return 'text-blue-500 animate-pulse';
      case 'polling': return 'text-yellow-500 animate-pulse';
      case 'complete': return 'text-green-500';
      case 'timeout': return 'text-orange-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'scraping': return 'Scraping...';
      case 'polling': return `Polling (${pollCount}/${maxPolls})`;
      case 'complete': return 'Complete';
      case 'timeout': return 'Timeout';
      case 'error': return 'Error';
      default: return 'Waiting for form activity...';
    }
  };

  const renderTimeline = () => {
    if (timeline.length === 0) return null;

    return (
      <div className="mb-6 pb-6 border-b border-gray-200">
        <h3 className="font-bold text-base mb-3 text-gray-900">
          🔄 Live Activity Timeline
        </h3>
        <div className="space-y-2">
          {timeline.map((event, idx) => {
            const elapsed = Math.floor((event.timestamp.getTime() - (startTime || Date.now())) / 1000);
            const isProgress = event.type === 'progress';

            return (
              <div
                key={idx}
                className={`flex items-start gap-2 text-sm ${
                  isProgress ? 'text-blue-600 font-medium' : 'text-gray-700'
                }`}
              >
                <span className="flex-1">{event.label}</span>
                <span className="text-gray-500 text-xs">({Math.abs(elapsed)}s)</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Desktop Layout */}
      <div className="hidden lg:block fixed right-0 top-0 h-screen z-[1000]">
        <div
          className={`h-full bg-white border-l border-gray-200 shadow-2xl transition-all duration-300 ${
            isCollapsed ? 'w-12' : 'w-[400px]'
          }`}
        >
          {/* Collapse Toggle Button */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full bg-white border border-gray-200 rounded-l-lg p-2 shadow-lg hover:bg-gray-50 transition-colors"
          >
            {isCollapsed ? (
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            )}
          </button>

          {!isCollapsed && (
            <div className="h-full overflow-y-auto p-6">
              {/* Header */}
              <div className="mb-6">
                <h2 className="font-bold text-lg text-gray-900 mb-2">
                  🔍 Scraper Debug Panel
                </h2>
                <p className="text-xs text-gray-500">
                  Read-only monitor - watching form activity
                </p>
              </div>

              {/* Status & Timer */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Status:</span>
                  <span className={`text-sm font-semibold ${getStatusColor()}`}>
                    {getStatusText()}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Elapsed:</span>
                  <span className="text-sm font-semibold text-gray-600">
                    {elapsedTime > 0 ? `${elapsedTime.toFixed(1)}s` : '0.0s'}
                  </span>
                </div>
                {nmls && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">NMLS:</span>
                    <span className="text-sm font-semibold text-gray-900">{nmls}</span>
                  </div>
                )}
                {draftId && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-600 mb-1">Draft ID:</div>
                    <div className="font-mono text-xs text-gray-900 break-all">{draftId}</div>
                  </div>
                )}
              </div>

              {/* Timeline */}
              {renderTimeline()}

              {/* Results Display */}
              {results && status === 'complete' && (
                <div className="mb-6">
                  <div className="mb-4">
                    <h3 className="font-bold text-base text-gray-900 mb-2">
                      📊 Extraction Results
                    </h3>
                    <div className="text-sm text-green-600 font-semibold">
                      ✅ {countTotalFields(results)} fields extracted successfully
                    </div>
                  </div>

                  {renderCategory('PROFILE IDENTITY', '👤', profileIdentityFields)}
                  {renderCategory('LOCATION', '📍', locationFields)}
                  {renderCategory('PRODUCTION STATS', '💰', productionStatsFields)}
                  {renderCategory('CONTACT INFO - INTERNAL', '📞', contactInfoFields)}
                  {renderCategory('SOCIAL LINKS', '🔗', socialLinksFields)}
                  {renderCategory('DEBUG INFO', '🐛', debugInfoFields)}

                  {/* JSON Viewer */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowJson(!showJson)}
                      className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors mb-3"
                    >
                      {showJson ? 'Hide Full JSON' : 'View Full JSON'}
                    </button>

                    {showJson && (
                      <div className="relative">
                        <button
                          onClick={copyToClipboard}
                          className="absolute top-2 right-2 p-2 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                          title="Copy to clipboard"
                        >
                          {copied ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-600" />
                          )}
                        </button>
                        <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-96 overflow-y-auto">
                          {JSON.stringify(results, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Waiting state */}
              {status === 'idle' && (
                <div className="text-center py-12 text-gray-400">
                  <p className="text-sm mb-2">👀 Watching for form activity...</p>
                  <p className="text-xs">Enter an NMLS number in the form to begin</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[1000]">
        <div
          className={`bg-white border-t border-gray-200 shadow-2xl transition-all duration-300 ${
            isCollapsed ? 'h-14' : 'h-[50vh]'
          }`}
        >
          {/* Collapse Toggle Button */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-full py-3 bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
          >
            <span className="font-semibold text-sm text-gray-700">
              {isCollapsed ? '🔍 Open Debug Panel' : '🔍 Close Debug Panel'}
            </span>
          </button>

          {!isCollapsed && (
            <div className="h-[calc(100%-56px)] overflow-y-auto p-4">
              {/* Same content as desktop but more compact */}
              <div className="mb-4">
                <h2 className="font-bold text-base text-gray-900 mb-1">
                  🔍 Scraper Debug Panel
                </h2>
                <p className="text-xs text-gray-500">
                  Watching form activity
                </p>
              </div>

              {/* Status */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Status:</span>
                  <span className={`text-sm font-semibold ${getStatusColor()}`}>
                    {getStatusText()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Elapsed:</span>
                  <span className="text-sm font-semibold text-gray-600">
                    {elapsedTime > 0 ? `${elapsedTime.toFixed(1)}s` : '0.0s'}
                  </span>
                </div>
              </div>

              {/* Timeline */}
              {renderTimeline()}

              {/* Results */}
              {results && status === 'complete' && (
                <div className="mb-4">
                  <div className="text-sm text-green-600 font-semibold mb-3">
                    ✅ {countTotalFields(results)} fields extracted
                  </div>
                  {renderCategory('PROFILE IDENTITY', '👤', profileIdentityFields)}
                  {renderCategory('LOCATION', '📍', locationFields)}
                  {renderCategory('PRODUCTION STATS', '💰', productionStatsFields)}
                  {renderCategory('CONTACT INFO', '📞', contactInfoFields)}
                  {renderCategory('SOCIAL LINKS', '🔗', socialLinksFields)}
                  {renderCategory('DEBUG INFO', '🐛', debugInfoFields)}
                </div>
              )}

              {status === 'idle' && (
                <div className="text-center py-8 text-gray-400">
                  <p className="text-sm">Waiting for form activity...</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
